/**
 * 会话相关工具类
 */
import { Employee, Group, NoticeMessageRespType, Session, SessionType, UID } from "@/types/chat";
import chatConfig from "@/components/chat/config";
import { convertEmployeeToString, employeeToUid, MultiCardTemplateType } from "@/utils/chat/message";
import pick from "lodash/pick";
import { ListItemData } from "@/components/ListItem";
import isFunction from "lodash/isFunction";
import { getDeptPositionDesc } from "@/models/user";
import ImService from "@/server/ImService";
import CHAT_NOTICE from "@/contant/chat";
import { convertUidToString, getSenderBySingleSession } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import { setUserTypeChange } from "@/components/Tags";

/**
 * 校验是否为AI助手会话
 * @param session
 * @returns boolean
 */
export function isAINoticeSession(session: Session) {
  const { userId } = (session.info as Employee) || {};
  return userId === CHAT_NOTICE.AI_PIN;
}

export async function isAIChatNoticeSessionId(sessionId: string) {
  if (!sessionId) {
    return false;
  }
  const args = sessionId.split(":");
  const user = await ImService.getInstance()?.getCurrentEmployeeLocal();
  const uid = getSenderBySingleSession(
    sessionId,
    convertUidToString(employeeToUid(user as Employee))
  );
  return uid?.pin === CHAT_NOTICE.AI_PIN && args[args.length - 1] === "chat";
}

export async function getAIChatWinSessionId () {
  const user = await ImService.getInstance()?.getCurrentEmployeeLocal();
  return `aiRobot:dd.notice:focus.bot:${convertEmployeeToString(user as Employee)}:chat`;
}

export async function getAIChatSessionId() {
  const user = await ImService.getInstance()?.getCurrentEmployeeLocal();
  return ImService.getInstance()?.generateSingleSessionId(user as Employee, {
    pin: CHAT_NOTICE.AI_CHAT_PIN,
    app: user?.app || "",
    teamId: user?.teamId || ""
  });
}

export function isAIChatSession(session: Session) {
  if (!session?.sessionId) {
    return false;
  }
  const args = session?.sessionId.split(":");
  return isAINoticeSession(session) && args[args.length - 1] === "chat";
}

export function isOrgChatSession(session: Session) {
  if (!session?.sessionId) {
    return false;
  }
  return [SessionType.ORG_NORMAL, SessionType.ORG_BROADCAST].includes(session.sessionType);
}

export async function isAIChatSessionId(sessionId: string) {
  if (!sessionId) {
    return false;
  }
  const user = await ImService.getInstance()?.getCurrentEmployeeLocal();
  const uid = getSenderBySingleSession(
    sessionId,
    convertUidToString(employeeToUid(user as Employee))
  );
  return uid?.pin === CHAT_NOTICE.AI_CHAT_PIN;
}
/**
 * 校验是否为自定义独立会话
 * @param session
 * @param customAppList // 自定义独立会话消息列表
 * @returns boolean
 */
export function isCustomAppSession(session: Session, customAppList: Session[]) {
  const { userId } = (session.info as Employee) || {};
  if (!customAppList.length) return false;
  const isCustomApp = customAppList
    ? customAppList.findIndex((item: any) => (item.info as Employee).userId === userId) > -1
    : false;
  return isCustomApp;
}

export function isCustomAppSessionPin(pin: string, customAppList: Session[]) {
  if (!customAppList.length) return false;
  const isCustomApp = customAppList
    ? customAppList.findIndex((item: any) => (item.info as Employee).userId === pin) > -1
    : false;
  return isCustomApp;
}

/**
 * 是否是业务组
 * @param session
 * @returns
 */
export function isBusinessGroup(session: Session) {
  if (!session || !session.isGroup || !session.info) {
    return false;
  }
  const info = session.info as any;
  return !!info.businessId;
}

export function isAINoticeSessionKey(sessionId: string) {
  return sessionId.includes(CHAT_NOTICE.AI_PIN);
}

/**
 * 校验是否为系统通知会话
 * @param session
 * @returns boolean
 */
export function isSYSNoticeSession(session: Session) {
  const { userId } = session.info as Employee;
  return userId === CHAT_NOTICE.SYS_PIN;
}

/**
 * 校验是否为专班通知
 * @param session
 * @returns boolean
 */
export function isAnnNoticeSession(session: Session) {
  const { userId } = session.info as Employee;
  return userId === CHAT_NOTICE.ANNOUNCEMENT_PIN;
}

/**
 * 检验是否为ai助手的子会话
 * @param session
 * @param customAppList // 自定义独立会话消息列表
 * @returns boolean
 */
export function isAINoticeChildSession(session: Session, customAppList: Session[]) {
  let { userId } = session.info as Employee;
  const isCustomApp = isCustomAppSession(session, customAppList);
  return (
    session.isNotice &&
    userId !== CHAT_NOTICE.SYS_PIN &&
    userId !== CHAT_NOTICE.ANNOUNCEMENT_PIN &&
    userId !== CHAT_NOTICE.AI_PIN &&
    !isCustomApp
  );
}

/**
 * 过滤不必要的会话
 * @param session
 */
export function isUnnecessarySession(session: Session) {
  if (session.isSingle) {
    if (isAIChatSession(session)) {
      return true;
    }
    // 非置顶且del为1的数据，解决未读消息角标的问题
    // if(!session?.settings?.top && session.del === 1){
    //   console.log("getAllSessionList===isUnnecessarySession", session);
    //   return true;
    // }
    const { userId } = session.info as Employee;
    return chatConfig["chat-session-invisible-list"].includes(userId);
  }
  return false;
}

// /**
//  * 过滤出通知类的会话列表
//  * @param allSessionList
//  * @returns
//  */
// export function filterNoticeSession(allSessionList: Session[]) {
//     return allSessionList.filter((item) => item.isNotice);
// }

export function getSelectorSessions(
  allSessionList: Session[],
  currentEmployee: any,
  filter?: (item: Session) => boolean
): ListItemData[] {
  // const imService = ImService.getInstance();
  // const currentEmployee = await imService.getCurrentEmployee();
  // eslint-disable-next-line complexity
  return allSessionList.reduce((result: any, item: Session) => {
    if (isFunction(filter) && !filter(item)) {
      return result;
    }
    if (
      (item.isGroup && (item as any).info?.flag === 3 && (item as any).info?.status === 1) ||
      (item.isSingle && !item.isNotice && item.info?.name && !item.isSecret)
    ) {
      const data = {
        id: item.sessionId,
        isGroup: item.isGroup,
        name: item.info.name,
        avatar: item.info.avatar,
        sessionId: item.sessionId
      };
      if (!data.isGroup) {
        const employee = item.info as Employee;
        const employeeExt = employee?.employeeExt || {};
        const userType = setUserTypeChange(
          { ...item.info, userType: employeeExt.userType },
          currentEmployee
        );
        Object.assign(data, {
          id: convertEmployeeToString(employee),
          sessionId: item.sessionId,
          desc: getDeptPositionDesc({
            deptName: employee?.e_dept_full_name || employee.deptName || "",
            titleName: employee?.e_position || employee.titleName || "",
            userId: employee?.userId,
            currentEmployee: currentEmployee,
            unitName: employee?.unitName,
            userType
          }),
          userType,
          remark: employeeExt.remark,
          name: item.info.name,
          labelInfoList: employee?.labelInfoList || [],
          ...employeeToUid(employee)
        });
      } else {
        const group = item.info as Group;
        const groupInfo = {
          ...group,
          desc: (group.rosterSize || 0) + "人" || "",
          groupType:
            group.groupType ||
            (group.kind === 512 ? 1 : group.kind === 1024 && group.subKind === 6 ? 2 : 3)
        };
        Object.assign(
          data,
          pick(groupInfo, ["desc", "groupType", "gid", "kind", "subKind", "business"])
        );
      }
      result.push(data);
    }
    return result;
  }, [] as ListItemData[]);
}

export const findUserRemarkBySession = (key: string, session: Session) => {
  try {
    const rosters = (session.info as any).rosters;
    const target = rosters[key];
    const employeeExt = target.info?.employeeExt;
    return employeeExt?.remark || "";
  } catch (error) {
    return "";
  }
};

export const mapSessions = getSelectorSessions;
